<script>
const FIELD_TYPES = [
  { value: 'text', label: 'Texte court' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'checkbox', label: 'Case à cocher' },
];

function slugify(value) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}

function makeField() {
  return {
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: [],
  };
}

export default {
  props: {
    field: { type: Object, default: null },
    existingNames: { type: Array, default: () => [] },
  },
  static: { FIELD_TYPES },
  data() {
    const f = this.field
      ? {
        name: this.field.name,
        label: this.field.label,
        type: this.field.type,
        required: !!this.field.required,
        options: Array.isArray(this.field.options) ? [...this.field.options] : [],
      }
      : makeField();
    return {
      form: f,
      autoName: !this.field,
      isLoading: false,
      touchedOptions: [],
    };
  },
  computed: {
    fieldTypes() {
      return this.$options.static.FIELD_TYPES;
    },
    isEditing() {
      return !!this.field;
    },
    nameError() {
      if (this.isEditing) return null;
      if (!this.form.name) return null;
      const others = (this.existingNames || []).filter((n) => n !== (this.field && this.field.name));
      if (others.includes(this.form.name)) {
        return 'Un champ avec cet identifiant existe déjà.';
      }
      return null;
    },
    optionsError() {
      if (this.form.type !== 'select') return null;
      const raw = this.form.options || [];
      // Only check empty on touched options
      const hasTouchedEmpty = raw.some((o, i) => this.touchedOptions.includes(i) && !(o || '').trim());
      if (hasTouchedEmpty) {
        return 'Une option ne peut pas être vide.';
      }
      if (raw.some((o) => (o || '').trim().length > 60)) {
        return 'Une option ne peut pas dépasser 60 caractères.';
      }
      const opts = raw.map((o) => o.trim());
      if (new Set(opts).size !== opts.length) {
        return 'Deux options ne peuvent pas être identiques.';
      }
      return null;
    },
    // For submit validation, check all options including non-touched
    hasEmptyOptions() {
      const raw = this.form.options || [];
      return raw.some((o) => !(o || '').trim());
    },
    canSubmit() {
      if (!this.form.label || !this.form.name) return false;
      if (this.nameError) return false;
      if (this.form.type === 'select' && (this.form.options || []).filter(Boolean).length === 0) {
        return false;
      }
      if (this.optionsError || this.hasEmptyOptions) return false;
      return true;
    },
  },
  methods: {
    onLabelInput() {
      if (this.isEditing) return;
      if (this.autoName) {
        this.form.name = slugify(this.form.label);
      }
    },
    addOption() {
      this.form.options.push('');
    },
    touchOption(index) {
      if (!this.touchedOptions.includes(index)) {
        this.touchedOptions.push(index);
      }
    },
    removeOption(index) {
      this.form.options.splice(index, 1);
      this.touchedOptions = this.touchedOptions
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i));
    },
    submit() {
      if (!this.canSubmit) return;
      const payload = {
        name: this.form.name,
        label: this.form.label,
        type: this.form.type,
        required: !!this.form.required,
        options: this.form.type === 'select'
          ? (this.form.options || []).filter(Boolean)
          : [],
      };
      this.$emit('save', payload);
    },
  },
};
</script>

<template>
  <form class="modal-card" style="width: 640px;" @submit.prevent="submit">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ isEditing ? 'Modifier la question' : 'Ajouter une question' }}
      </p>
    </header>
    <section class="modal-card-body">
      <b-field
        label="Libellé affiché aux apprenants"
        :type="nameError ? 'is-danger' : ''"
        :message="nameError || ''"
      >
        <b-input
          v-model="form.label"
          required
          placeholder="Ex: Votre numéro de SIRET"
          @input="onLabelInput"
          maxlength="100"
        />
      </b-field>

      <div class="columns">
        <b-field class="column" label="Type de Champ">
          <b-select v-model="form.type" expanded>
            <option v-for="t in fieldTypes" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </b-select>
        </b-field>
        <b-field class="column" label="Obligatoire ?">
          <b-switch v-model="form.required">
            {{ form.required ? 'Oui' : 'Non' }}
          </b-switch>
        </b-field>
      </div>

      <template v-if="form.type === 'select'">
        <b-field label="Options de la liste (l'apprenant ne pourra en sélectionner qu'une seule)">
          <div class="w-full">
            <div
              v-for="(opt, optIndex) in form.options"
              :key="optIndex"
              class="is-flex mb-2"
              style="gap: 8px;"
            >
              <b-input
                v-model="form.options[optIndex]"
                :placeholder="`Option ${optIndex + 1}`"
                maxlength="60"
                :has-counter="false"
                expanded
                @blur="touchOption(optIndex)"
              />
              <b-button
                type="is-text"
                icon-left="trash"
                @click="removeOption(optIndex)"
              />
            </div>
            <b-button
              type="is-light"
              icon-left="plus"
              size="is-small"
              @click="addOption"
            >
              Ajouter une option
            </b-button>
            <p v-if="optionsError" class="help is-danger">
              {{ optionsError }}
            </p>
          </div>
        </b-field>
      </template>
    </section>
    <footer class="modal-card-foot is-justify-content-flex-end">
      <b-button @click="$emit('close')">Annuler</b-button>
      <b-button
        type="is-primary"
        native-type="submit"
        :disabled="!canSubmit"
        :loading="isLoading"
      >
        {{ isEditing ? 'Mettre à jour' : 'Ajouter' }}
      </b-button>
    </footer>
  </form>
</template>
